import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, finalize, map } from 'rxjs';
import { MainHTTPService } from './http/main-http.service';
import { GetCustomerScheduleTemplatesForEditOutput, Template } from '../_models/Monitoring/template';
import { DeviceTemplate } from '../_models/Devices/Device';
import { AssignOverrideDto, TimezoneList } from '../_models/Monitoring/assign-override';
import { CalendarSchedule, ICalendarScheduleModelData, ScheduleExceptionDto, UpdateGroupScheduleExceptionDto } from '../_models/Monitoring/Schedules';
import { TableColumn } from '../../shared/core/Model/Table/TableColumn';
import { DeleteOverrideDto, Override, OverrideResult } from '../_models/Monitoring/Overrides';
import { SearchOverrideParameters } from '../_models/search-stealth/search-page-models-v2';
import { CreateEditGuardTour, GuardTour } from '../_models/Monitoring/GuardTourData';
import { OverridesConfig } from '../../shared/core/Model/Config/Overrides.config';

@Injectable({
  providedIn: 'root'
})
export class MonitoringService {

  // public fields
  private isLoadingSubject: BehaviorSubject<boolean>;
  private isSavingSubject: BehaviorSubject<boolean>;

  get isLoading$(): Observable<boolean> {
    return this.isLoadingSubject.asObservable();
  }

  get isSaving$(): Observable<boolean> {
    return this.isSavingSubject.asObservable();
  }

  get overridesColumns(): TableColumn[] {
    return OverridesConfig.columns;
  }

  get pdfColumns(): TableColumn[] {
    return OverridesConfig.pdfColumns;
  }

  get overrideDetailsColumns(): TableColumn[] {
    return OverridesConfig.detailsColumns;
  }

  constructor(private mainHttpService: MainHTTPService)
  {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isSavingSubject = new BehaviorSubject<boolean>(false);
  }

  //#region SCHEDULES
  /**
  * This is the get function for getting the customer calendar schedules
  * @param customerId This is the customerId parameter
  * @returns returns the customer calendar schedules
  */
  getCalendarSchedules(customerId?: number | undefined, siteId?: number | undefined, nvrId?: number | undefined): Observable<CalendarSchedule[]> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getCalendarSchedules(customerId, siteId, nvrId)
            .pipe(
              map((schedule: ICalendarScheduleModelData) => {
                const calendarSchedule = schedule.result;
                return calendarSchedule;
              }),
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  /**
  * This is the get function for getting the customer calendar schedules
  * @param customerId This is the customerId parameter
  * @returns returns the customer calendar schedules mapped for full calendar
  */
  getCustomerCalendarSchedules(customerId: number): Observable<CalendarSchedule[]> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getCustomerCalendarSchedules(customerId)
            .pipe(
              map((schedule: ICalendarScheduleModelData) => {
                const calendarSchedule = schedule.result;
                return calendarSchedule;
              }),
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  /**
  * This is the get function for getting the site calendar schedules
  * @param siteId This is the siteId parameter
  * @returns returns the site calendar schedules mapped for full calendar
  */
  getSiteCalendarSchedules(siteId: number): Observable<CalendarSchedule[]> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getSiteCalendarSchedules(siteId)
            .pipe(
              map((schedule: ICalendarScheduleModelData) => {
                const calendarSchedule = schedule.result
                return calendarSchedule;
              }),
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  
  /**
  * This is the get function for getting the site guard tour calendar schedules
  * @param siteId This is the siteId parameter
  * @returns returns the site guard tour calendar schedules mapped for full calendar
  */
  getSiteGuardTourCalendarSchedules(siteId: number): Observable<CalendarSchedule[]> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getSiteGuardTourCalendarSchedules(siteId)
            .pipe(
              map((schedule: ICalendarScheduleModelData) => {
                const calendarSchedule = schedule.result
                return calendarSchedule;
              }),
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  /**
  * This is the get function for getting the nvr calendar schedules
  * @param nvrId This is the nvrId parameter
  * @returns returns the nvr calendar schedules mapped for full calendar
  */
  getNVRCalendarSchedules(nvrId: number): Observable<CalendarSchedule[]> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getNVRCalendarSchedules(nvrId)
            .pipe(
              map((schedule: ICalendarScheduleModelData) => {
                const calendarSchedule = schedule.result;
                return calendarSchedule;
              }),
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  /**
   * This is the function to create or edit a schedule override from the calendar
   * @param input The parameters needed to create or edit a schedule override
   * @param isUpdate
   * @returns
   */
  createOrEditExceptions(input: ScheduleExceptionDto[], isUpdate?: boolean): Observable<void> {
    this.isSavingSubject.next(true);
    return this.mainHttpService.createOrEditExceptions(input, isUpdate)
            .pipe(
              finalize(() => this.isSavingSubject.next(false))
            );
  }

  /**
   * This is the function to delete a schedule override from the calendar
   * @param input The parameters needed to delete a schedule override
   * @returns
   */
  deleteExceptions(input: ScheduleExceptionDto[] ): Observable<void> {
    this.isSavingSubject.next(true);
    return this.mainHttpService.deleteExceptions(input)
            .pipe(
              finalize(() => this.isSavingSubject.next(false))
            );
  }

  getTemplates(): Observable<Template[]> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getTemplates()
            .pipe(
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  createTemplate(input: Template | null | undefined): Observable<void> {
    this.isSavingSubject.next(true);
    return this.mainHttpService.createTemplate(input)
            .pipe(
              finalize(() => this.isSavingSubject.next(false))
            );
  }

  editTemplate(input: Template | null | undefined): Observable<void> {
    this.isSavingSubject.next(true);
    return this.mainHttpService.editTemplate(input)
            .pipe(
              finalize(() => this.isSavingSubject.next(false))
            );
  }

  deleteTemplate(templateId: number): Observable<void> {
    this.isSavingSubject.next(true);
    return this.mainHttpService.deleteTemplate(templateId)
            .pipe(
              finalize(() => this.isSavingSubject.next(false))
            );
  }

  getDevicesAssignedToTemplates(templateId: number | null | undefined): Observable<GetCustomerScheduleTemplatesForEditOutput> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getDevicesAssignedToTemplates(templateId)
            .pipe(
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  getCustomerScheduleTemplates(customerId: number | null | undefined): Observable<GetCustomerScheduleTemplatesForEditOutput> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getCustomerScheduleTemplates(customerId)
            .pipe(
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  getSiteScheduleTemplates(siteId: number | null | undefined): Observable<GetCustomerScheduleTemplatesForEditOutput> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getSiteScheduleTemplates(siteId)
            .pipe(
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  getNVRScheduleTemplates(nvrId: number | null | undefined): Observable<GetCustomerScheduleTemplatesForEditOutput> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getNVRScheduleTemplates(nvrId)
            .pipe(
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  getCameraScheduleTemplates(cameraId: number | null | undefined): Observable<GetCustomerScheduleTemplatesForEditOutput> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getCameraScheduleTemplates(cameraId)
            .pipe(
              finalize(() => this.isLoadingSubject.next(false))
            );
  }

  assignDeviceScheduleTemplate(templateId: number | null | undefined, input: Array<DeviceTemplate> | null | undefined): Observable<void> {
    this.isSavingSubject.next(true);
    return this.mainHttpService.assignDeviceScheduleTemplate(templateId, input)
            .pipe(
              finalize(() => this.isSavingSubject.next(false))
            );
  }

  
  deleteGroupScheduleOverride(input: AssignOverrideDto | null | undefined): Observable<void> {
    return this.mainHttpService.deleteGroupScheduleOverride(input);
  }

  createGroupScheduleOverride(input: AssignOverrideDto | null | undefined): Observable<void> {
    return this.mainHttpService.createGroupScheduleOverride(input);
  }

  getOverrideDetails(parameters: SearchOverrideParameters): Observable<OverrideResult> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getOverrideDetails(parameters)
      .pipe(
        map((result: any) => {
          return result;
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  deleteOverride(input: DeleteOverrideDto): Observable<void> {
    this.isSavingSubject.next(true);
    return this.mainHttpService.deleteOverride(input)
            .pipe(
              finalize(() => this.isSavingSubject.next(false))
            );
  }

  updateOverride(input: UpdateGroupScheduleExceptionDto): Observable<void> {
    this.isSavingSubject.next(true);
    return this.mainHttpService.updateOverride(input)
            .pipe(
              finalize(() => this.isSavingSubject.next(false))
            );
  }

  getOverrides(parameters: SearchOverrideParameters, hasFilters:boolean = false): Observable<OverrideResult> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getOverrides(parameters, hasFilters)
      .pipe(
        map((result: OverrideResult) => {
          return result;
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  getOverridesWithDetails(parameters: SearchOverrideParameters): Observable<OverrideResult> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getOverridesWithDetails(parameters)
      .pipe(
        map((result: OverrideResult) => {
          return result;
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  getGuardTours(advancedFilters: string | null | undefined, sorting?: string | null | undefined, skipCount?: number | null | undefined, maxResultCount?: number | null | undefined): Observable<GuardTour[]> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getGuardTours(advancedFilters, sorting, skipCount, maxResultCount)
      .pipe(
        map((result: any[]) => {
          let guardTours = result.map(r => Object.assign(new GuardTour(), r));

          return guardTours;
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  getGuardTourCountsByNVR(deviceIds: Array<number>): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getGuardTourCountsByNVR(deviceIds)
        .pipe(
          finalize(() => this.isLoadingSubject.next(false))
        );
  }

  getGuardTourTimezoneLookup(): Observable<TimezoneList[]> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getGuardTourTimezoneLookup()
        .pipe(
          finalize(() => this.isLoadingSubject.next(false))
        );
  }

  getGuardTourForEdit(id: number): Observable<CreateEditGuardTour> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getGuardTourForEdit(id)
        .pipe(
          finalize(() => this.isLoadingSubject.next(false))
        );
  }

  createOrEditGuardTour(input: CreateEditGuardTour | null | undefined): Observable<void> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.createOrEditGuardTour(input)
        .pipe(
          finalize(() => this.isLoadingSubject.next(false))
        );
  }
}
